import { useFormik } from 'formik';
import {
  ChangeEvent,
  Dispatch,
  FC,
  SetStateAction,
  useMemo,
  useState
} from 'react';
import styled from 'styled-components';
import { colors } from '../../../component/colors';
import { DropDownWithLabel } from '../../../component/dropDown';
import { SearchableDropDownWithLabel } from '../../../component/dropDown/searchableDropDownWthLabel';
import { GooglePlace } from '../../../component/googlePlace';
import { ImagePicker } from '../../../component/imagePicker';
import { Input } from '../../../component/input';
import { AddModal } from '../../../component/modal';
import { TextAreaWithLabel } from '../../../component/textArea';
import { useFetchAgentQuery } from '../../../store/agent/api';
import { useAddHotelMutation } from '../../../store/hotel/api';
import { facilities, hotelRatings, hotleTypes } from '../../../utils/module';
import { states } from '../../../utils/nigeriaSates';
import { pxToRem } from '../../../utils/pxToRem';
import { showErrorToast, showSuccessToast } from '../../../utils/toast';
import { generateUrl } from '../../../utils/urlGenerator';
import useDebounce from '../../../utils/useDebounce';
import { addHotelValidation } from './validation';

type IAddHotel = {
  show?: boolean;
  setShow: Dispatch<SetStateAction<boolean>>;
};
type FormikValues = {
  name: string;
  address: {
    address: string;
    lat: number;
    lng: number;
    city: string;
  };
  numberOfRooms: number;
  type: string;
  hotelRating: string;
  facilities: string[];
  agent: {
    name: string;
    index: number;
  };
  state: string;
  file: File[] | null;
  description: string;
};
export const AddHotel: FC<IAddHotel> = ({ setShow }) => {
  const [searchAgent, setSearchAgent] = useState('');
  const debounceSearchAgent = useDebounce(searchAgent, 500);
  const { data: agents } = useFetchAgentQuery(
    generateUrl({ module: 'hotel', search: debounceSearchAgent })
  );
  const [addHotel, { isLoading: isAdding }] = useAddHotelMutation();

  const agentsList = useMemo(() => {
    return (
      agents?.data.fetchedData.map((agents) => ({
        name: agents.name,
        imageUrl: agents.file.url
      })) ?? []
    );
  }, [agents]);
  const handleFilePicked = (file: File[]) => {
    setFieldValue('file', file);
  };

  const handleAddHotel = (values: FormikValues) => {
    if (!agents?.data) return;
    if (!values.file) return showErrorToast('hotel picture is required');
    const payload = new FormData();
    payload.append('name', values.name);
    payload.append('type', values.type.toLocaleLowerCase().replace(' ', '-'));
    payload.append('description', values.description);
    payload.append('agent', agents.data.fetchedData[values.agent.index]._id);
    payload.append('noOfRooms', values.numberOfRooms.toString());
    values.file.forEach((file) => {
      payload.append('file', file);
    });
    payload.append('location[state]', values.state);
    payload.append('location[city]', values.address.city);
    payload.append('location[address]', values.address.address);
    payload.append(
      'location[coordinates][longitude]',
      values.address.lng.toString()
    );
    payload.append(
      'location[coordinates][latitude]',
      values.address.lat.toString()
    );
    for (const item of values.facilities) {
      payload.append('facilitiesAndServices', item);
    }
    addHotel(payload)
      .unwrap()
      .then((res) => {
        showSuccessToast(res.message);
        setShow(false);
      })
      .catch((error) => {
        showErrorToast(error.message);
      });
  };
  const { values, errors, touched, setFieldValue, handleChange, handleSubmit } =
    useFormik({
      initialValues: {
        name: '',
        address: { address: '', lat: 0, lng: 0, city: '' },
        numberOfRooms: 1,
        type: hotleTypes[0],
        hotelRating: hotelRatings[0],
        facilities: [] as Array<string>,
        agent: { name: '', index: 0 },
        state: states[0],
        file: null,
        description: ''
      },
      validationSchema: addHotelValidation,
      onSubmit: handleAddHotel
    });
  const handlRemoveFacilities = (facility: string) => {
    setFieldValue(
      'facilities',
      values.facilities.filter((item) => item !== facility)
    );
  };
  const handleSelectFacilities = (
    e: ChangeEvent<HTMLInputElement>,
    facility: string
  ) => {
    if (e.target.checked) {
      setFieldValue('facilities', [...values.facilities, facility]);
    } else {
      handlRemoveFacilities(facility);
    }
  };

  return (
    <AddModal
      title="Add Hotel"
      disableBtn={isAdding}
      btnTxt={isAdding ? 'Loading...' : 'Add hotel'}
      btnClick={handleSubmit}
      closeModal={() => setShow(false)}
      isLoading={isAdding}
    >
      <Container>
        <Row>
          <CustomInput
            labelText="Name"
            value={values.name}
            placeholder={'Enter name'}
            onChange={handleChange('name')}
            errorMessage={touched.name ? errors.name : ''}
          />
          <CustomDropDown
            label="Type"
            value={values.type}
            onSelect={(item) => {
              setFieldValue('module', item);
            }}
            options={hotleTypes}
          />
        </Row>
        <Row>
          <CustomGooglePlace
            onSelect={(value) => setFieldValue('address', value)}
            label={'Address'}
            placeholder={'Enter address'}
            errorMessage={touched.address && errors.address ? '*required' : ''}
          />
          <CustomDropDown
            label="State"
            value={values.state}
            onSelect={(item) => {
              setFieldValue('state', item);
            }}
            options={states}
          />
        </Row>
        <Row>
          <CustomInput
            labelText={'Number of rooms'}
            value={values.numberOfRooms}
            type={'number'}
            min={1}
            onChange={handleChange('numberOfRooms')}
            errorMessage={touched.numberOfRooms ? errors.numberOfRooms : ''}
          />
          <CustomDropDown
            label="Hotel rating"
            value={values.hotelRating}
            onSelect={(item) => {
              setFieldValue('hotelRating', item);
            }}
            options={hotelRatings}
          />
        </Row>
        <Row>
          <CustomDropDown
            label={'Facilities & services'}
            value={''}
            placeholder={'Select facilities & services'}
          >
            {facilities.map((item, index) => (
              <DropDownItem key={index.toString()}>
                <CheckBox
                  onChange={(e) => handleSelectFacilities(e, item)}
                  checked={values.facilities.includes(item)}
                  type={'checkbox'}
                />
                {item}
              </DropDownItem>
            ))}
          </CustomDropDown>
          <SearchableCustomDropDown
            search={searchAgent}
            setSearch={setSearchAgent}
            label="SOJI agent"
            value={values.agent.name}
            onSelect={(item, index) => {
              setFieldValue('agent', { name: item, index });
            }}
            options={agentsList}
            errorMessage={touched.agent && errors.agent ? '*required' : ''}
          />
        </Row>
        <SeletedFacilitiesContainer>
          {values.facilities.map((item, index) => (
            <SeletedFacilities
              onClick={() => handlRemoveFacilities(item)}
              key={index.toString()}
            >{`${item}  x`}</SeletedFacilities>
          ))}
        </SeletedFacilitiesContainer>
        <CustomTextAreaWithLabel
          labelText="Description"
          placeholder="Enter description"
          value={values.description}
          onChange={handleChange('description')}
          errorMessage={touched.description ? errors.description : ''}
        />
        <CustomImageUpload onFilePicked={handleFilePicked} />
      </Container>
    </AddModal>
  );
};

const Container = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: 0px 40px;
  padding-bottom: 32px;
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;

  @media screen and (max-width: 500px) {
    display: block;
  }
`;

const CustomInput = styled(Input)`
  flex: 0.46;
  color: ${colors.textDark};
  margin-bottom: 16px;
`;
const CustomGooglePlace = styled(GooglePlace)`
  flex: 0.46;
  margin-bottom: 16px;
`;
const CustomDropDown = styled(DropDownWithLabel)`
  flex: 0.46;
  margin-bottom: 16px;
`;
const CustomImageUpload = styled(ImagePicker)``;

const DropDownItem = styled.div`
  background-color: transparent;
  border: none;
  text-align: left;
  width: 100%;
  margin: 16px 23px;
`;
const CheckBox = styled.input``;

const SeletedFacilitiesContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 16px;
`;
const SeletedFacilities = styled.p`
  font-weight: bold;
  font-size: ${pxToRem(10)};
  line-height: ${pxToRem(16)};
  align-items: center;
  color: #a6b7d4;
  background-color: #ebf2fa;
  border-radius: 6px;
  padding: 4px 10px;
  margin-right: 8px;
  cursor: pointer;
  &:hover {
    opacity: 0.7;
  }
`;
const CustomTextAreaWithLabel = styled(TextAreaWithLabel)`
  margin-bottom: 16px;
`;
const SearchableCustomDropDown = styled(SearchableDropDownWithLabel)`
  flex: 0.46;
  margin-bottom: 16px;
`;
